import placeholderThumbnail from 'assets/kestrel-photo-placeholder.svg'
import { useEffect, useState } from 'react'
import { KVPhoto } from 'types'

type PhotoUrlType = 'thumbnail' | 'fullSize'

export const usePhotoUrl = (
  photo: KVPhoto | null,
  type: PhotoUrlType
): {
  url: string | null
  isLoading: boolean
  isLoadingHighRes: boolean
  error: string | null
} => {
  const [url, setUrl] = useState<string | null>(null)
  const [fullResUrl, setFullResUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHighRes, setIsLoadingHighRes] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!photo) {
      setUrl(null)
      setFullResUrl(null)
      setIsLoading(false)
      setIsLoadingHighRes(false)
      setError(null)
      return
    }

    // Reset URL when photo changes to avoid showing stale image
    setUrl(null)
    setError(null)

    let isMounted = true
    let fetchedUrl: string | null = null
    let fetchedFullResUrl: string | null = null

    // Progressive loading for raw images
    if (type === 'fullSize') {
      setIsLoading(true)
      setIsLoadingHighRes(true)
      setError(null)

      let downsampledSucceeded = false
      const hasAnyFullSize = photo.hasDownsampled || photo.hasRaw

      // If no full-size versions available, exit loading state immediately
      if (!hasAnyFullSize) {
        setIsLoading(false)
        setIsLoadingHighRes(false)
        setError('No full-size image available')
        return () => {
          isMounted = false
        }
      }

      // Load downsampled first
      if (photo.hasDownsampled) {
        photo
          .getDownsampledUrl()
          .then((downsampledUrl) => {
            if (isMounted) {
              downsampledSucceeded = true
              fetchedUrl = downsampledUrl
              setUrl(downsampledUrl)
              setIsLoading(false)
            }
          })
          .catch(() => {
            // If downsampled fails, full-res will still be attempted
            // Error handling done in the full-res catch block
          })
      }

      // Load raw in background
      if (photo.hasRaw) {
        photo
          .getFullSizeUrl()
          .then((rawUrl) => {
            if (isMounted) {
              fetchedFullResUrl = rawUrl
              setFullResUrl(rawUrl)
              setIsLoadingHighRes(false)
              // Swap to full-res when ready
              setUrl(rawUrl)
              setIsLoading(false)
              // Revoke the downsampled URL to free memory
              if (fetchedUrl && fetchedUrl !== rawUrl) {
                URL.revokeObjectURL(fetchedUrl)
              }
            }
          })
          .catch((err) => {
            if (isMounted) {
              setIsLoadingHighRes(false)
              if (downsampledSucceeded) {
                // If raw fails but downsampled succeeded, show downsampled
                console.warn(
                  'Failed to load raw photo, using downsampled:',
                  err
                )
              } else {
                // Both failed - set error
                setError(err.message || 'Failed to load photo')
                setIsLoading(false)
              }
            }
          })
      }

      return () => {
        isMounted = false
        if (fetchedUrl) {
          URL.revokeObjectURL(fetchedUrl)
        }
        if (fetchedFullResUrl) {
          URL.revokeObjectURL(fetchedFullResUrl)
        }
      }
    } else {
      // For thumbnails, prefer the stored thumbnail when it exists, otherwise fallback to placeholder
      if (!photo.hasThumbnail) {
        setUrl(placeholderThumbnail)
        setIsLoading(false)
        setError(null)
        return () => {
          isMounted = false
        }
      }

      setIsLoading(true)
      setError(null)

      photo
        .getThumbnailUrl()
        .then((resultUrl) => {
          if (isMounted) {
            fetchedUrl = resultUrl
            setUrl(resultUrl)
            setIsLoading(false)
          }
        })
        .catch((err) => {
          if (isMounted) {
            setError(err.message || 'Failed to load photo URL')
            setIsLoading(false)
          }
        })

      return () => {
        isMounted = false
        if (fetchedUrl) {
          URL.revokeObjectURL(fetchedUrl)
        }
      }
    }
  }, [photo, type])

  useEffect(() => {
    return () => {
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url)
      }
      if (fullResUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fullResUrl)
      }
    }
  }, [url, fullResUrl])

  return { url, isLoading, isLoadingHighRes, error }
}
